import { httpClient } from '@/shared/libs/httpClient';
import { mockHttpClient } from '@/shared/libs/mockHttpClient';
import { publicHttpClient } from '@/shared/libs/publicHttpClient';

// Switch between real API clients and mock client depending on environment variable
const USE_MOCK = process.env.NEXT_PUBLIC_API_IMPL === 'mock';

// Secure client: requires authentication (token or cookies)
// Open client: public API without authentication
const secureClient = USE_MOCK ? mockHttpClient : httpClient;
const openClient = USE_MOCK ? mockHttpClient : publicHttpClient;

/**
 * Le corps d'une requête, encodé — **sauf un `FormData`, qui passe tel quel** (#2238).
 *
 * `JSON.stringify(formData)` rend `{}` : la requête part vide, sans erreur, et le serveur répond
 * « fichier manquant » sur un envoi qui contenait bien le fichier. Le dépôt de la procédure de
 * déclaration est le premier envoi multipart à passer par une mutation ; jusqu'ici les rares
 * uploads appelaient `httpClient` directement, ce qui leur faisait **perdre la garde d'écriture**
 * de `useSecureMutation` — un tenant en lecture seule pouvait déposer un fichier.
 *
 * `httpClient` laisse déjà le navigateur poser le `Content-Type` et sa frontière quand le corps est
 * un `FormData` : il n'y a rien à faire de plus ici que de ne pas l'abîmer.
 */
const encodeBody = (body: unknown): BodyInit =>
  body instanceof FormData ? body : JSON.stringify(body);

/**
 * apiClient: wrapper around HTTP methods
 * Automatically selects between secure (authenticated) or open (public) client
 */
export const apiClient = {
  get: <T>(url: string, secure = true, init?: RequestInit) =>
    secure ? secureClient<T>(url, init) : openClient<T>(url),

  post: <T>(url: string, body: any, secure = true, init?: RequestInit) =>
    secure
      ? secureClient<T>(url, { method: 'POST', body: encodeBody(body), ...(init || {}) })
      : openClient<T>(url, { method: 'POST', body: encodeBody(body), ...(init || {}) }),

  put: <T>(url: string, body: any, secure = true, init?: RequestInit) =>
    secure
      ? secureClient<T>(url, {
          method: 'PUT',
          body: encodeBody(body),
          ...(init || {}),
        })
      : openClient<T>(url, {
          method: 'PUT',
          body: encodeBody(body),
          ...(init || {}),
        }),

  patch: <T>(url: string, body: any, secure = true, init?: RequestInit) =>
    secure
      ? secureClient<T>(url, {
          method: 'PATCH',
          body: encodeBody(body),
          ...(init || {}),
        })
      : openClient<T>(url, {
          method: 'PATCH',
          body: encodeBody(body),
          ...(init || {}),
        }),

  delete: <T>(url: string, secure = true, init?: RequestInit) =>
    secure
      ? secureClient<T>(url, { method: 'DELETE', ...(init || {}) })
      : openClient<T>(url, { method: 'DELETE', ...(init || {}) }),
};
